<template>
	<div class="calendar">
		<div class="calendar__header">
			<DxButton
				class="calendar__nav-btn"
				styling-mode="text"
				icon="chevronleft"
				type="default"
				@click="prevYear" />
			<DxButton
				v-if="calendarType !== 'month'"
				class="calendar__nav-btn"
				styling-mode="text"
				icon="arrowleft"
				type="default"
				@click="prevMonth" />
			<span class="calendar__title">
				{{
					calendarType === 'month' ? currentYear : monthName + ' ' + currentYear
				}}
			</span>
			<DxButton
				v-if="calendarType !== 'month'"
				class="calendar__nav-btn"
				styling-mode="text"
				icon="arrowright"
				type="default"
				@click="nextMonth" />
			<DxButton
				class="calendar__nav-btn"
				styling-mode="text"
				icon="chevronright"
				type="default"
				@click="nextYear" />
		</div>
		<!-- Vista de meses -->
		<div v-if="calendarType === 'month'" class="calendar__months-grid">
			<div
				v-for="(month, idx) in monthsOfYear"
				:key="month"
				class="calendar__month"
				:class="{
					'calendar__month--selected': isMonthSelected(idx),
					'calendar__month--disabled': !isMonthActive(idx),
				}"
				@click="selectMonth(idx)">
				{{ month }}
			</div>
		</div>
		<!-- Vista de días/semanas -->
		<div
			v-else
			:class="[
				'calendar__grid',
				showWeekNumbers
					? 'calendar__grid--with-weeks'
					: 'calendar__grid--no-weeks',
			]">
			<div v-if="showWeekNumbers" class="calendar__week-label">Sem</div>
			<div class="calendar__day" v-for="day in daysOfWeek" :key="day">
				{{ day }}
			</div>
			<template v-for="(row, rowIdx) in calendarRows">
				<div
					v-if="showWeekNumbers"
					class="calendar__week"
					:key="'week-' + rowIdx"
					:class="{
						'calendar__week--selected': isWeekSelected(row.week),
						'calendar__week--disabled': !isWeekSelectable(row.days),
					}"
					@click="selectWeek(row.week, row.days)">
					{{ row.week }}
				</div>
				<div
					v-for="date in row.days"
					:key="date.key"
					class="calendar__date"
					:class="{
						'calendar__date--empty': !date.day,
						'calendar__date--selected': !showWeekNumbers && isSelected(date),
						'calendar__date--disabled': !isActive(date),
						'calendar__date--readonly': showWeekNumbers && isActive(date),
					}"
					@click="selectDate(date)">
					{{ date.day }}
				</div>
			</template>
		</div>
	</div>
</template>

<script>
	import DxButton from 'devextreme-vue/button';
	import moment from 'moment';
	import { getLocalizedMonthNames } from '../../js/utils/months';
	export default {
		name: 'Calendar',
		components: {
			DxButton,
		},
		props: {
			initialDate: {
				type: Date,
				default: () => new Date(),
			},
			multiselect: {
				type: Boolean,
				default: false,
			},
			activeStart: {
				type: Date,
				default: null,
			},
			activeEnd: {
				type: Date,
				default: null,
			},
			calendarType: {
				type: String,
				default: 'day',
			},
		},
		data() {
			return {
				currentMonth: this.initialDate.getMonth(),
				currentYear: this.initialDate.getFullYear(),
				selectedDates: [],
				selectedWeeks: [],
				selectedMonths: [],
				daysOfWeek: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
				monthsOfYear: getLocalizedMonthNames(),
			};
		},
		computed: {
			monthName() {
				return this.monthsOfYear[this.currentMonth];
			},
			calendarRows() {
				// Genera filas con número de semana y días
				const firstDay = new Date(this.currentYear, this.currentMonth, 1);
				const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
				let startDay = firstDay.getDay();
				startDay = startDay === 0 ? 6 : startDay - 1;

				const totalDays = lastDay.getDate();
				const days = [];
				for (let i = 0; i < startDay; i++) {
					days.push({ day: '', key: `empty-${i}` });
				}
				for (let d = 1; d <= totalDays; d++) {
					const isToday =
						d === new Date().getDate() &&
						this.currentMonth === new Date().getMonth() &&
						this.currentYear === new Date().getFullYear();
					days.push({ day: d, key: d, isToday });
				}
				// Agrupa en semanas
				const rows = [];
				for (let i = 0; i < days.length; i += 7) {
					const weekDays = days.slice(i, i + 7);
					// Busca el primer día real de la semana para calcular el número
					const firstRealDay = weekDays.find((d) => d.day);
					let weekNumber = '';
					if (firstRealDay && firstRealDay.day) {
						const dateObj = new Date(
							this.currentYear,
							this.currentMonth,
							firstRealDay.day
						);
						weekNumber = this.getWeekNumber(dateObj);
					}
					rows.push({ week: weekNumber, days: weekDays });
				}
				return rows;
			},
			showWeekNumbers() {
				return this.calendarType === 'week';
			},
		},
		methods: {
			prevMonth() {
				if (this.currentMonth === 0) {
					this.currentMonth = 11;
					this.currentYear--;
				} else {
					this.currentMonth--;
				}
			},
			nextMonth() {
				if (this.currentMonth === 11) {
					this.currentMonth = 0;
					this.currentYear++;
				} else {
					this.currentMonth++;
				}
			},
			prevYear() {
				this.currentYear--;
			},
			nextYear() {
				this.currentYear++;
			},
			selectDate(date) {
				if (!date.day || !this.isActive(date)) return;
				const selected = moment([
					this.currentYear,
					this.currentMonth,
					date.day,
				]).format('YYYY-MM-DD');

				if (this.multiselect) {
					const idx = this.selectedDates.indexOf(selected);
					if (idx === -1) {
						this.selectedDates.push(selected);
					} else {
						this.selectedDates.splice(idx, 1);
					}
				} else {
					this.selectedDates = [selected];
				}
				this.$emit('update:selectedDates', this.selectedDates.slice());
			},
			isWeekSelectable(weekDays) {
				return weekDays.some((day) => day.day && this.isActive(day));
			},
			selectWeek(weekNumber, weekDays) {
				if (!weekNumber || !this.isWeekSelectable(weekDays)) return;
				const idx = this.selectedWeeks.indexOf(weekNumber);

				// Obtén el primer día (lunes) de la semana ISO
				const firstDateStr = moment()
					.year(this.currentYear)
					.isoWeek(weekNumber)
					.startOf('isoWeek')
					.format('YYYY-MM-DD');

				if (idx === -1) {
					this.selectedWeeks.push(weekNumber);
					if (!this.selectedDates.includes(firstDateStr)) {
						this.selectedDates.push(firstDateStr);
					}
				} else {
					this.selectedWeeks.splice(idx, 1);
					const dateIdx = this.selectedDates.indexOf(firstDateStr);
					if (dateIdx !== -1) {
						this.selectedDates.splice(dateIdx, 1);
					}
				}
				this.$emit('update:selectedWeeks', this.selectedWeeks.slice());
				this.$emit('update:selectedDates', this.selectedDates.slice());
			},
			isWeekSelected(weekNumber) {
				return this.selectedWeeks.includes(weekNumber);
			},
			isSelected(date) {
				if (!date.day) return false;
				const compare = moment([
					this.currentYear,
					this.currentMonth,
					date.day,
				]).format('YYYY-MM-DD');
				return this.selectedDates.includes(compare);
			},
			isActive(date) {
				if (!date.day) return false;
				if (!this.activeStart || !this.activeEnd) return true;
				const current = moment([this.currentYear, this.currentMonth, date.day]);
				const start = moment(this.activeStart).startOf('day');
				const end = moment(this.activeEnd).endOf('day');
				return current.isSameOrAfter(start) && current.isSameOrBefore(end);
			},
			getWeekNumber(date) {
				// ISO 8601 week number
				return moment(date).isoWeek();
			},
			isMonthActive(monthIdx) {
				if (!this.activeStart || !this.activeEnd) return true;
				const monthDate = moment([this.currentYear, monthIdx, 1]);
				const start = moment(this.activeStart).startOf('month');
				const end = moment(this.activeEnd).endOf('month');
				return monthDate.isSameOrAfter(start) && monthDate.isSameOrBefore(end);
			},
			selectMonth(monthIdx) {
				if (!this.isMonthActive(monthIdx)) return;
				const idx = this.selectedMonths.indexOf(monthIdx);

				// Fecha día 1 del mes seleccionado
				const firstDateStr = moment([this.currentYear, monthIdx, 1]).format(
					'YYYY-MM-DD'
				);

				if (idx === -1) {
					this.selectedMonths.push(monthIdx);
					if (!this.selectedDates.includes(firstDateStr)) {
						this.selectedDates.push(firstDateStr);
					}
				} else {
					this.selectedMonths.splice(idx, 1);
					const dateIdx = this.selectedDates.indexOf(firstDateStr);
					if (dateIdx !== -1) {
						this.selectedDates.splice(dateIdx, 1);
					}
				}
				this.$emit('update:selectedMonths', this.selectedMonths.slice());
				this.$emit('update:selectedDates', this.selectedDates.slice());
			},
			isMonthSelected(monthIdx) {
				return this.selectedMonths.includes(monthIdx);
			},
		},
	};
</script>
<style lang="scss" scoped>
	@import './calendar.scss';
</style>
